import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../utils/api';

const AccountingSuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    vatNumber: '',
    fiscalCode: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    country: 'Italia',
    phone: '',
    email: '',
    pec: '',
    sdiCode: '',
    notes: ''
  });

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nel caricamento fornitori');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleOpenNew = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      vatNumber: '',
      fiscalCode: '',
      address: '',
      city: '',
      postalCode: '',
      province: '',
      country: 'Italia',
      phone: '',
      email: '',
      pec: '',
      sdiCode: '',
      notes: ''
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name || '',
      vatNumber: supplier.vatNumber || '',
      fiscalCode: supplier.fiscalCode || '',
      address: supplier.address || '',
      city: supplier.city || '',
      postalCode: supplier.postalCode || '',
      province: supplier.province || '',
      country: supplier.country || 'Italia',
      phone: supplier.phone || '',
      email: supplier.email || '',
      pec: supplier.pec || '',
      sdiCode: supplier.sdiCode || '',
      notes: supplier.notes || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplier(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, formData);
      } else {
        await api.post('/suppliers', formData);
      }
      fetchSuppliers();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await api.delete(`/suppliers/${supplierToDelete.id}`);
      fetchSuppliers();
      setOpenDeleteDialog(false);
      setSupplierToDelete(null);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nell\'eliminazione');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Rubrica fornitori</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew}>
          Aggiungi fornitore
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ragione sociale</TableCell>
                  <TableCell>P.IVA</TableCell>
                  <TableCell>Cod. fiscale</TableCell>
                  <TableCell>Città</TableCell>
                  <TableCell>Telefono</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nessun fornitore. Clicca &quot;Aggiungi fornitore&quot; per iniziare.
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.vatNumber || '-'}</TableCell>
                      <TableCell>{s.fiscalCode || '-'}</TableCell>
                      <TableCell>{s.city || '-'}</TableCell>
                      <TableCell>{s.phone || '-'}</TableCell>
                      <TableCell>{s.email || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(s)} title="Modifica">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(s)} title="Elimina" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSupplier ? 'Modifica fornitore' : 'Nuovo fornitore'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField name="name" label="Ragione sociale *" fullWidth value={formData.name} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="vatNumber" label="Partita IVA" fullWidth value={formData.vatNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="fiscalCode" label="Codice fiscale" fullWidth value={formData.fiscalCode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField name="address" label="Indirizzo" fullWidth value={formData.address} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="city" label="Città" fullWidth value={formData.city} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="postalCode" label="CAP" fullWidth value={formData.postalCode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField name="province" label="Provincia" fullWidth value={formData.province} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="phone" label="Telefono" fullWidth value={formData.phone} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="email" label="Email" fullWidth type="email" value={formData.email} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="pec" label="PEC" fullWidth value={formData.pec} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="sdiCode" label="Codice destinatario SDI" fullWidth value={formData.sdiCode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField name="notes" label="Note" fullWidth multiline rows={2} value={formData.notes} onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || !formData.name.trim()}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Elimina fornitore</DialogTitle>
        <DialogContent>
          Eliminare il fornitore &quot;{supplierToDelete?.name}&quot;? Questa azione non può essere annullata.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annulla</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountingSuppliersPage;
